import React from 'react';
import { MailX, Send } from 'lucide-react';

/**
 * A customer-facing send was REFUSED because the recipient is on the email
 * provider's suppression list — a 409 with `code: 'recipient_suppressed'`
 * from the invoice, quote and ticket send doors.
 *
 * Why this is not a toast. A toast says "something went wrong" and vanishes;
 * this refusal says "this address is dead, here is the date it died, and a
 * resend will be dropped without notice". The office needs the sentence in
 * front of them while they correct the address — and needs the one escape
 * hatch this component offers:
 *
 *   SEND ANYWAY (`force: true`). The provider's list is account-wide and
 *   nothing on our side can clear it, so a mailbox that was genuinely fixed
 *   under the same spelling is unsendable forever without this. A forced send
 *   is a REAL attempt: it is logged as one and the provider's verdict stands.
 *   If the mailbox is still dead the send is dropped exactly as before — the
 *   button does not make mail arrive, it makes the attempt honest.
 *
 * The shape is shared with the ticket doors (`suppressedRefusalBody` in the
 * API's lib/email-suppression.ts).
 */
export interface SuppressedRefusal {
  error: string;
  suppressed: Array<{ email: string; bounceType: 'Permanent' | 'Suppressed'; loggedAt: string }>;
  canForce?: boolean;
}

/** The refusal on an axios rejection, or null when the error is anything else. */
export function suppressedRefusalOf(error: unknown): SuppressedRefusal | null {
  const res = (error as any)?.response;
  if (res?.status !== 409) return null;
  const body = res.data;
  if (body?.code !== 'recipient_suppressed') return null;
  return {
    error: String(body.error ?? 'The recipient is on the email provider’s suppression list.'),
    suppressed: Array.isArray(body.suppressed) ? body.suppressed : [],
    canForce: body.canForce !== false,
  };
}

export function SuppressedRecipientNotice({
  refusal,
  documentLabel,
  onForce,
  onDismiss,
  busy = false,
}: {
  refusal: SuppressedRefusal;
  /** "invoice" / "quote" — the noun in the button and the caution line. */
  documentLabel: string;
  onForce: () => void;
  onDismiss: () => void;
  busy?: boolean;
}) {
  return (
    <div
      role="alert"
      className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-3"
      data-testid="suppressed-recipient-notice"
    >
      <div className="flex items-start gap-3">
        <MailX size={18} className="text-red-400 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-red-400 text-sm font-semibold">Not sent — recipient is on the suppression list</p>
          <p className="text-sm text-fieldiq-text mt-1">{refusal.error}</p>
        </div>
      </div>
      {refusal.canForce && (
        <div className="flex flex-wrap items-center gap-2 pl-[30px]">
          <button
            type="button"
            onClick={onForce}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fieldiq-card border border-fieldiq-border text-white text-xs hover:border-fieldiq-accent transition disabled:opacity-50"
          >
            <Send size={13} />
            {busy ? 'Sending…' : `Send anyway — the mailbox has been fixed`}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-xs text-fieldiq-muted hover:text-white transition disabled:opacity-50"
          >
            Dismiss
          </button>
          <p className="basis-full text-xs text-fieldiq-muted">
            Only if the address is correct and the inbox is working again. A forced send is a real attempt: the
            provider decides, and if the mailbox is still dead this {documentLabel} is dropped without notice.
          </p>
        </div>
      )}
    </div>
  );
}

export default SuppressedRecipientNotice;
